import React, { useCallback, useEffect, useState } from 'react';

import ErrorState from '../../../commodity/components/ui/ErrorState';
import LoadingState from '../../../commodity/components/ui/LoadingState';
import { PortalPage } from '../../../design-system/portal';
import ReviewsSection from '../components/ReviewsSection';
import { getTeacherDashboard } from '../api/teacher-dashboard';
import { getTeacherReviews } from '../api/reviews';

/**
 * What families have said about this teacher (Epic 12).
 *
 * The teacher's own id comes from the dashboard payload rather than from the
 * auth slice. That looks indirect and is deliberate: courses, enrollments and
 * reviews are all keyed by the Cognito `sub`, while the auth user carries the
 * DynamoDB `id`, and confusing the two is the identity split that hid teachers
 * from the catalog and stopped trust scores reaching listings. The dashboard
 * endpoint already resolves it server-side, so this asks the one component
 * that is known to be right.
 */
const TeacherReviews: React.FC = () => {
  const [teacherId, setTeacherId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dashboard = await getTeacherDashboard();
      setTeacherId(dashboard.teacherId);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PortalPage screenId="s_reviews">
      <h1 style={{ marginBottom: 4 }}>Reseñas</h1>
      <p style={{ color: 'var(--text-2)', marginTop: 0, marginBottom: 20 }}>
        Lo que las familias han dicho de tus clases. Solo puede reseñarte una familia que haya
        tomado al menos una sesión contigo.
      </p>

      {loading && <LoadingState what="tus reseñas" />}
      {error ? <ErrorState action="cargar tus reseñas" error={error} onRetry={load} /> : null}

      {teacherId && (
        <ReviewsSection
          subject="TEACHER"
          subjectId={teacherId}
          load={getTeacherReviews}
          title="Reseñas recibidas"
        />
      )}
    </PortalPage>
  );
};

export default TeacherReviews;
